import { useState, useEffect } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  listEmailTemplates,
  getEmailTemplate,
  updateEmailTemplate,
  resetEmailTemplate,
  testEmailTemplate,
} from "@/api/admin.api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Mail, RotateCcw, Send, Save, Eye } from "lucide-react";

export function EmailTemplatesPage() {
  const qc = useQueryClient();
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [subject, setSubject] = useState("");
  const [html, setHtml] = useState("");
  const [testTo, setTestTo] = useState("");
  const [feedback, setFeedback] = useState<string | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const { data: templates = [] } = useQuery({
    queryKey: ["email-templates"],
    queryFn: listEmailTemplates,
  });

  const { data: detail } = useQuery({
    queryKey: ["email-template", selectedKey],
    queryFn: () => getEmailTemplate(selectedKey!),
    enabled: !!selectedKey,
  });

  // When the selected template's detail arrives, hydrate the edit form. We
  // reset every time the selected key flips so the user never sees stale
  // values from the previous template.
  useEffect(() => {
    if (detail) {
      setSubject(detail.subject);
      setHtml(detail.html);
      setShowPreview(false);
      setFeedback(null);
    }
  }, [detail?.key]);

  // Auto-pick the first template when the list arrives.
  useEffect(() => {
    if (!selectedKey && templates.length > 0) {
      setSelectedKey(templates[0].key);
    }
  }, [templates, selectedKey]);

  const saveMutation = useMutation({
    mutationFn: () => updateEmailTemplate(selectedKey!, { subject, html }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-templates"] });
      qc.invalidateQueries({ queryKey: ["email-template", selectedKey] });
      setFeedback("Sablon kaydedildi.");
      setTimeout(() => setFeedback(null), 2500);
    },
  });

  const resetMutation = useMutation({
    mutationFn: () => resetEmailTemplate(selectedKey!),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["email-templates"] });
      qc.invalidateQueries({ queryKey: ["email-template", selectedKey] });
      setFeedback("Default'a sifirlandi.");
      setTimeout(() => setFeedback(null), 2500);
    },
  });

  const testMutation = useMutation({
    mutationFn: () => testEmailTemplate(selectedKey!, testTo),
    onSuccess: () => {
      setFeedback(`Test ${testTo} adresine gonderildi.`);
      setTimeout(() => setFeedback(null), 3000);
    },
    onError: () => {
      setFeedback("Test gonderilemedi.");
      setTimeout(() => setFeedback(null), 3000);
    },
  });

  const dirty = detail && (detail.subject !== subject || detail.html !== html);

  return (
    <div className="grid gap-4 md:grid-cols-[280px_1fr]">
      {/* Template list */}
      <div className="space-y-2">
        <h1 className="mb-3 flex items-center gap-2 text-xl font-bold">
          <Mail className="size-5" />
          Email Sablonlari
        </h1>
        {templates.map((t) => (
          <button
            key={t.key}
            onClick={() => setSelectedKey(t.key)}
            className={`w-full rounded-lg border p-3 text-left transition-colors ${
              selectedKey === t.key ? "border-primary bg-primary/5" : "hover:bg-muted"
            }`}
          >
            <div className="flex items-center justify-between gap-2">
              <span className="font-semibold text-sm">{t.key}</span>
              {t.isCustomized && (
                <Badge variant="outline" className="text-[10px]">
                  Ozellesmis
                </Badge>
              )}
            </div>
            <p className="mt-1 text-xs text-muted-foreground line-clamp-2">{t.description}</p>
          </button>
        ))}
      </div>

      {/* Editor */}
      <div>
        {!detail ? (
          <Card>
            <CardContent className="py-8 text-center text-muted-foreground">
              Sablon secin
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="space-y-4 pt-6">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground">{detail.key}</p>
                <p className="text-sm">{detail.description}</p>
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">Konu</label>
                <Input value={subject} onChange={(e) => setSubject(e.target.value)} />
              </div>

              <div>
                <label className="mb-1 block text-sm font-medium">HTML icerik</label>
                <Textarea
                  value={html}
                  onChange={(e) => setHtml(e.target.value)}
                  rows={14}
                  className="font-mono text-xs"
                />
              </div>

              <div className="rounded-lg bg-muted/40 p-3">
                <p className="mb-2 text-xs font-semibold uppercase text-muted-foreground">
                  Kullanilabilir degiskenler
                </p>
                <div className="flex flex-wrap gap-1.5">
                  {detail.variables.length === 0 ? (
                    <span className="text-xs text-muted-foreground">Bu sablonda degisken yok</span>
                  ) : (
                    detail.variables.map((v) => (
                      <code
                        key={v}
                        className="rounded bg-background px-1.5 py-0.5 text-[11px]"
                      >{`{{${v}}}`}</code>
                    ))
                  )}
                </div>
              </div>

              {showPreview && (
                <div>
                  <p className="mb-1 text-xs font-semibold uppercase text-muted-foreground">
                    On izleme (degiskenler oldugu gibi gosterilir)
                  </p>
                  <iframe
                    srcDoc={html}
                    title="Preview"
                    className="h-72 w-full rounded-lg border bg-white"
                    sandbox=""
                  />
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2 border-t pt-4">
                <Button
                  size="sm"
                  onClick={() => saveMutation.mutate()}
                  disabled={!dirty || saveMutation.isPending}
                >
                  <Save className="mr-1 size-4" />
                  Kaydet
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setShowPreview((p) => !p)}
                >
                  <Eye className="mr-1 size-4" />
                  {showPreview ? "On izlemeyi kapat" : "On izle"}
                </Button>
                {detail.isCustomized && (
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      if (confirm("Sablon default'a dondurulecek. Devam edilsin mi?")) {
                        resetMutation.mutate();
                      }
                    }}
                    disabled={resetMutation.isPending}
                  >
                    <RotateCcw className="mr-1 size-4" />
                    Default'a Sifirla
                  </Button>
                )}
                <div className="ml-auto flex items-center gap-2">
                  <Input
                    type="email"
                    placeholder="test@email.com"
                    value={testTo}
                    onChange={(e) => setTestTo(e.target.value)}
                    className="w-48"
                  />
                  <Button
                    size="sm"
                    variant="outline"
                    disabled={!testTo || testMutation.isPending}
                    onClick={() => testMutation.mutate()}
                  >
                    <Send className="mr-1 size-4" />
                    Test Gonder
                  </Button>
                </div>
              </div>

              {feedback && (
                <p className="rounded-md bg-emerald-50 px-3 py-2 text-sm text-emerald-700">
                  {feedback}
                </p>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
